import type { InstrumentName } from './score.js';

/**
 * The music's voices (ADR-0056), made from oscillators and noise like the
 * game's sounds: nothing to download, nothing to license. Each is a few
 * nodes that start, shape a note and stop themselves.
 *
 * The levels are the mockups' (ADR-0056): changing one moves the balance
 * inside every track that uses it, and the tracks' trims were measured against
 * these.
 */

/** Where an instrument plays into: its bus, and what it may share. */
export interface Voices {
  readonly c: BaseAudioContext;
  /** A second of white noise, for drums and breath. */
  readonly noise: AudioBuffer;
  bus(name: InstrumentName): AudioNode;
}

type Play = (v: Voices, t: number, f: number, seconds: number, velocity: number) => void;

function tone(
  c: BaseAudioContext,
  type: OscillatorType,
  f: number,
  t: number,
  end: number,
): OscillatorNode {
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f, t);
  o.start(t);
  o.stop(end);
  return o;
}

function gain(c: BaseAudioContext, value = 0): GainNode {
  const g = c.createGain();
  g.gain.value = value;
  return g;
}

function filter(c: BaseAudioContext, type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode {
  const b = c.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  return b;
}

/** Struck: up in `attack`, then away over `decay`. */
function strike(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

/** Held: up in `attack`, settling to `settle` of the peak by `t + seconds`, away over `release`. */
function hold(
  g: GainNode,
  t: number,
  peak: number,
  attack: number,
  seconds: number,
  release: number,
  settle = 1,
): void {
  const top = t + attack;
  const end = Math.max(top + 0.02, t + seconds);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, top);
  g.gain.exponentialRampToValueAtTime(Math.max(peak * settle, 0.0002), end);
  g.gain.exponentialRampToValueAtTime(0.0001, end + release);
}

function noise(v: Voices, t: number, length: number): AudioBufferSourceNode {
  const src = v.c.createBufferSource();
  src.buffer = v.noise;
  src.start(t, (t * 0.731) % 0.8);
  src.stop(t + length);
  return src;
}

/** A vibrato that comes in over `rise` seconds, on every oscillator given. */
function vibrato(
  c: BaseAudioContext,
  t: number,
  end: number,
  rate: number,
  depth: number,
  rise: number,
  targets: readonly OscillatorNode[],
): void {
  const lfo = tone(c, 'sine', rate, t, end);
  const amount = gain(c);
  amount.gain.setValueAtTime(0, t);
  amount.gain.linearRampToValueAtTime(depth, t + rise);
  lfo.connect(amount);
  for (const o of targets) amount.connect(o.frequency);
}

const vibes: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const g = gain(c);
  tone(c, 'sine', f, t, t + seconds + 1.8).connect(g);
  strike(g, t, 0.3 * vel, 0.004, Math.min(2.2, seconds + 1.1));
  const bar = gain(c);
  tone(c, 'sine', f * 4, t, t + 0.4).connect(bar);
  strike(bar, t, 0.045 * vel, 0.002, 0.16);
  g.connect(v.bus('vibes'));
  bar.connect(v.bus('vibes'));
};

const gtr: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const end = t + seconds + 0.45;
  const lp = filter(c, 'lowpass', Math.min(f * 7, 5000), 0.6);
  lp.frequency.setValueAtTime(Math.min(f * 7, 5000), t);
  lp.frequency.exponentialRampToValueAtTime(Math.max(f * 1.6, 300), t + 0.3);
  tone(c, 'triangle', f, t, end).connect(lp);
  const edge = gain(c, 0.25);
  tone(c, 'sawtooth', f, t, end).connect(edge);
  edge.connect(lp);
  const g = gain(c);
  hold(g, t, 0.1 * vel, 0.004, Math.max(seconds, 0.25), 0.3, 0.3);
  lp.connect(g);
  g.connect(v.bus('gtr'));
};

const bass: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const end = t + seconds + 0.3;
  const lp = filter(c, 'lowpass', 750);
  tone(c, 'triangle', f, t, end).connect(lp);
  const body = gain(c, 0.6);
  tone(c, 'sine', f, t, end).connect(body);
  body.connect(lp);
  const g = gain(c);
  hold(g, t, 0.36 * vel, 0.008, Math.max(seconds, 0.12), 0.16, 0.35);
  lp.connect(g);
  g.connect(v.bus('bass'));
};

const marimba: Play = (v, t, f, _seconds, vel) => {
  const c = v.c;
  const decay = Math.min(1.1, Math.max(0.35, 180 / f));
  const g = gain(c);
  tone(c, 'sine', f, t, t + decay + 0.1).connect(g);
  strike(g, t, 0.34 * vel, 0.002, decay);
  const knock = gain(c);
  tone(c, 'sine', f * 3.93, t, t + 0.15).connect(knock);
  strike(knock, t, 0.07 * vel, 0.001, 0.06);
  g.connect(v.bus('marimba'));
  knock.connect(v.bus('marimba'));
};

const pizz: Play = (v, t, f, _seconds, vel) => {
  const c = v.c;
  const end = t + 0.6;
  const lp = filter(c, 'lowpass', Math.min(f * 8, 6000), 1.1);
  lp.frequency.setValueAtTime(Math.min(f * 8, 6000), t);
  lp.frequency.exponentialRampToValueAtTime(f * 1.8, t + 0.15);
  tone(c, 'sawtooth', f, t, end).connect(lp);
  tone(c, 'sawtooth', f * 1.004, t, end).connect(lp);
  const g = gain(c);
  strike(g, t, 0.09 * vel, 0.002, 0.32);
  lp.connect(g);
  g.connect(v.bus('pizz'));
};

const bassoon: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const lp = filter(c, 'lowpass', 650, 2);
  tone(c, 'square', f, t, t + seconds + 0.15).connect(lp);
  const g = gain(c);
  hold(g, t, 0.13 * vel, 0.02, seconds, 0.07, 0.75);
  lp.connect(g);
  g.connect(v.bus('bassoon'));
};

const clarinet: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const end = t + seconds + 0.25;
  const o = tone(c, 'square', f, t, end);
  vibrato(c, t, end, 5.2, f * 0.003, 0.4, [o]);
  const lp = filter(c, 'lowpass', Math.min(f * 3.2, 2400), 0.7);
  o.connect(lp);
  const g = gain(c);
  hold(g, t, 0.06 * vel, 0.06, seconds, 0.15, 0.85);
  lp.connect(g);
  g.connect(v.bus('clarinet'));
};

const flute: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const end = t + seconds + 0.3;
  const o = tone(c, 'sine', f, t, end);
  const o2 = tone(c, 'triangle', f, t, end);
  vibrato(c, t, end, 4.8, f * 0.005, 0.45, [o, o2]);
  const g = gain(c);
  o.connect(g);
  const soft = gain(c, 0.18);
  o2.connect(soft);
  soft.connect(g);
  hold(g, t, 0.15 * vel, 0.07, seconds, 0.22, 0.8);
  const breath = gain(c);
  const bp = filter(c, 'bandpass', f * 2, 2);
  noise(v, t, 0.2).connect(bp);
  bp.connect(breath);
  strike(breath, t, 0.035 * vel, 0.01, 0.12);
  g.connect(v.bus('flute'));
  breath.connect(v.bus('flute'));
};

const harpsi: Play = (v, t, f, _seconds, vel) => {
  const c = v.c;
  const hp = filter(c, 'highpass', f * 0.8, 0.7);
  const lp = filter(c, 'lowpass', Math.min(f * 10, 7000), 0.7);
  for (const ratio of [1, 1.002]) tone(c, 'sawtooth', f * ratio, t, t + 0.9).connect(hp);
  hp.connect(lp);
  const g = gain(c);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(0.085 * vel, t + 0.002);
  g.gain.exponentialRampToValueAtTime(0.028 * vel, t + 0.08);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
  lp.connect(g);
  g.connect(v.bus('harpsi'));
};

const pad: Play = (v, t, f, seconds, vel) => {
  const c = v.c;
  const end = t + seconds + 1.2;
  const lp = filter(c, 'lowpass', 800, 0.5);
  for (const cents of [-7, 7]) {
    const o = tone(c, 'sawtooth', f, t, end);
    o.detune.value = cents;
    o.connect(lp);
  }
  const g = gain(c);
  hold(g, t, 0.022 * vel, 0.5, seconds, 1, 0.9);
  lp.connect(g);
  g.connect(v.bus('pad'));
};

const rim: Play = (v, t, _f, _seconds, vel) => {
  const c = v.c;
  const hp = filter(c, 'highpass', 1800);
  noise(v, t, 0.05).connect(hp);
  const g = gain(c);
  strike(g, t, 0.12 * vel, 0.001, 0.025);
  hp.connect(g);
  const knock = gain(c);
  tone(c, 'triangle', 1650, t, t + 0.06).connect(knock);
  strike(knock, t, 0.045 * vel, 0.001, 0.03);
  g.connect(v.bus('rim'));
  knock.connect(v.bus('rim'));
};

const shaker: Play = (v, t, _f, _seconds, vel) => {
  const c = v.c;
  const bp = filter(c, 'bandpass', 7000, 1.2);
  noise(v, t, 0.08).connect(bp);
  const g = gain(c);
  strike(g, t, 0.06 * vel, 0.006, 0.05);
  bp.connect(g);
  g.connect(v.bus('shaker'));
};

/** A woodblock, at the note written: kept under every chime the game plays. */
const wood: Play = (v, t, f, _seconds, vel) => {
  const c = v.c;
  const g = gain(c);
  tone(c, 'sine', f, t, t + 0.12).connect(g);
  strike(g, t, 0.11 * vel, 0.001, 0.07);
  const knock = gain(c);
  tone(c, 'sine', f * 2.4, t, t + 0.06).connect(knock);
  strike(knock, t, 0.04 * vel, 0.001, 0.03);
  g.connect(v.bus('wood'));
  knock.connect(v.bus('wood'));
};

const wind: Play = (v, t, _f, seconds, vel) => {
  const c = v.c;
  const bp = filter(c, 'bandpass', 350, 1.4);
  bp.frequency.setValueAtTime(350, t);
  bp.frequency.exponentialRampToValueAtTime(1100, t + seconds * 0.45);
  bp.frequency.exponentialRampToValueAtTime(380, t + seconds);
  const src = c.createBufferSource();
  src.buffer = v.noise;
  src.loop = true;
  src.start(t);
  src.stop(t + seconds + 0.1);
  src.connect(bp);
  const g = gain(c);
  hold(g, t, 0.05 * vel, seconds * 0.4, seconds * 0.55, seconds * 0.45, 0.8);
  bp.connect(g);
  g.connect(v.bus('wind'));
};

const tom: Play = (v, t, _f, _seconds, vel) => {
  const c = v.c;
  const o = tone(c, 'sine', 98, t, t + 0.7);
  o.frequency.exponentialRampToValueAtTime(68, t + 0.35);
  const g = gain(c);
  strike(g, t, 0.3 * vel, 0.004, 0.6);
  o.connect(g);
  g.connect(v.bus('tom'));
};

export const INSTRUMENTS: Readonly<Record<InstrumentName, Play>> = {
  vibes,
  gtr,
  bass,
  marimba,
  pizz,
  bassoon,
  clarinet,
  flute,
  harpsi,
  pad,
  rim,
  shaker,
  wood,
  wind,
  tom,
};

/** How much of each instrument goes to the room. */
export const SENDS: Readonly<Record<InstrumentName, number>> = {
  vibes: 0.35,
  gtr: 0.2,
  bass: 0.05,
  marimba: 0.3,
  pizz: 0.3,
  bassoon: 0.1,
  clarinet: 0.35,
  flute: 0.4,
  harpsi: 0.25,
  pad: 0.5,
  rim: 0.25,
  shaker: 0.15,
  wood: 0.3,
  wind: 0.4,
  tom: 0.15,
};

/** A second of white noise, the same every time. */
export function makeNoise(c: BaseAudioContext): AudioBuffer {
  const buffer = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 22222;
  for (let i = 0; i < data.length; i += 1) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    data[i] = seed / 0x3fffffff - 1;
  }
  return buffer;
}

/** A small room: decaying noise for the reverb. */
export function makeRoom(c: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(c.sampleRate * seconds);
  const buffer = c.createBuffer(2, length, c.sampleRate);
  let seed = 777;
  for (let ch = 0; ch < 2; ch += 1) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i += 1) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      const t = i / c.sampleRate;
      data[i] = (seed / 0x3fffffff - 1) * Math.exp(-t * 3.2) * (t < 0.012 ? t / 0.012 : 1);
    }
  }
  return buffer;
}
